import {
  Box,
  Button,
  ButtonBase,
  Flex,
  Stack,
  Divider,
  Icon,
  InputAdornment,
  InputControl,
  Link,
  Text,
  useColorMode,
  useColorStyle,
  Spinner,
  Space,
} from '@tonic-one/react';
import {
  LockIcon,
  UserIcon,
  ViewIcon,
  ViewOffIcon,
} from '@tonic-one/react-icons';
import React, { useCallback, useEffect, useState } from 'react';
import config from 'src/config';
import { CasAccountType, IdpNameToType, IdpTypeToName } from '@Types/app';
import { AccountApi } from '@Apis';
import { useNavigate } from 'react-router-dom';
import { InlineError } from '@Components/Common/FormikComponent';
import { ValidProps } from '@Types/index';
import { validateIsNotEmptyString } from '@Utils/validate';
import { useTranslation } from 'react-i18next';
import {
  getLocalStorageItemWithExpiry,
  getValidateMsg,
  setLocalStorageItemWithExpiry,
  validateEmail,
} from '@Utils/common';
import { IS_JP, IS_UAE } from '@Utils/const';
import { ResetPasswordStep } from '../index';
import ReCAPTCHA from 'react-google-recaptcha';

export const SignIn = ({
  setResetPasswordStep,
  setLoading,
}: {
  setResetPasswordStep: (value: ResetPasswordStep) => void;
  setLoading: (load: boolean) => void;
}) => {
  const [colorMode] = useColorMode();
  const { t } = useTranslation();
  const [colorStyle] = useColorStyle({ colorMode });
  const [reCaptchaFailCount, setReCaptchaFailCount] = useState<number>(0);
  const iconColor = colorStyle.color.tertiary;
  const redirectToAuth = useCallback(
    (tenantId: string) => {
      setLoading(true);
      if (tenantId.toUpperCase() === 'TM') {
        setRedirectLoadingMsg(t('login.redirecting.clpAuth'));
      } else {
        setRedirectLoadingMsg(t('login.redirecting.lmpAuth'));
      }
      window.location.href = config.ui.authUrl + tenantId;
    },
    [t, setLoading]
  );
  const navigate = useNavigate();
  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const [accountType, setAccountType] = useState<CasAccountType>(
    CasAccountType.None
  ); // ['local', 'cas'
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [view, setView] = useState(false);
  const toggleView = () => setView((view) => !view);
  const [redirectLoadingMsg, setRedirectLoadingMsg] = useState<
    string | undefined
  >(undefined);
  const [errorMsg, setErrorMsg] = React.useState<string | undefined>(undefined);
  const [passwordErrorMsg, setPasswordErrorMsg] = React.useState<
    string | undefined
  >(undefined);
  const setButtonLoadingCallback = useCallback((value: boolean) => {
    setButtonLoading(value);
  }, []);
  const [loginErrorMsg, setLoginErrorMsg] = useState<string | undefined>(
    undefined
  );
  const validateName: ValidProps[] = [
    {
      validMethod: validateIsNotEmptyString,
      errMsg: t('login.username.validate.isEmpty'),
    },
  ];

  const validatePassword: ValidProps[] = [
    {
      validMethod: validateIsNotEmptyString,
      errMsg: t('login.password.validate.isEmpty'),
    },
  ];

  useEffect(() => {
    const count = getLocalStorageItemWithExpiry(
      `${username}_reCaptchaFailCount`
    );
    if (count) {
      setReCaptchaFailCount(parseInt(count));
    }
  }, [username]);
  const validatePass = (value: string) => {
    const msg = getValidateMsg(validatePassword, value);
    setPasswordErrorMsg(msg);
    return msg;
  };

  const validateUsername = (value: string) => {
    const msg = getValidateMsg(validateName, value);
    console.log('validateUsername', msg, value);
    setErrorMsg(msg);
    return msg;
  };

  const checkAccountMigration = useCallback(async () => {
    try {
      const data: { isMigrating: boolean; redirectUrl?: string } = (
        await AccountApi.checkAccountMigrationInfo(username)
      ).data;
      if (data.redirectUrl) {
        setButtonLoadingCallback(true);
        window.location.href = data.redirectUrl;
      }
    } catch (error) {
      console.error(error);
    }
  }, [username, setButtonLoadingCallback]);
  const checkAccountType = useCallback(async () => {
    try {
      setLoading(true);
      await checkAccountMigration();
      const res: {
        userType: CasAccountType;
        ssoConfig?: {
          redirectUrl: string;
          ssoType: string;
        };
      } = (await AccountApi.getAccountType(username)).data;
      if (res && res.userType) {
        if (res.ssoConfig) {
          let serviceName = '';
          if (
            res.ssoConfig.ssoType === IdpTypeToName[IdpNameToType.AzureADOrAdfs]
          ) {
            serviceName = 'Active Directory';
          } else if (
            res.ssoConfig.ssoType === IdpTypeToName[IdpNameToType.Okta]
          ) {
            serviceName = 'Okta';
          } else if (
            res.ssoConfig.ssoType === IdpTypeToName[IdpNameToType.Google]
          ) {
            serviceName = 'Google';
          } else if (
            res.ssoConfig.ssoType === IdpTypeToName[IdpNameToType.PingOne]
          ) {
            serviceName = 'PingOne';
          }
          setRedirectLoadingMsg(
            t('login.redirect.SP.info', { service: serviceName })
          );
          setLoading(true);
          window.location.href = res.ssoConfig.redirectUrl;
          return;
        }
        setAccountType(res.userType);
        return res.userType;
      }
      return CasAccountType.None;
    } catch (error) {
      console.log(error);
      return CasAccountType.None;
    } finally {
      setLoading(false);
    }
  }, [setLoading, username, t, checkAccountMigration]);

  useEffect(() => {
    const searchParams = new URL(window.location.toString()).searchParams;
    const tenantId = searchParams.get('tenant_id') || 'TM';
    if (accountType === CasAccountType.ClpAccount) {
      redirectToAuth(tenantId);
    }
    const reason = searchParams.get('reason');
    if (reason === 'common.ERRORS.CAN_NOT_LOGIN_CUZ_UPGRADE_V1') {
      setLoginErrorMsg(t('login.signIn.error.canNotUpgradeCuzUpgradeV1'));
    }
  }, [accountType, redirectToAuth]);

  const handler = useCallback(async () => {
    const searchParams = new URL(window.location.toString()).searchParams;
    const tenantId = searchParams.get('tenant_id') || 'TM';
    if (!username) {
      return;
    }
    if (!validateEmail(username)) {
      setButtonLoadingCallback(true);
      redirectToAuth(tenantId);
      return;
    }
    const accountType = await checkAccountType();
    if (accountType === CasAccountType.ClpAccount) {
      setLoading(true);
      setButtonLoadingCallback(true);
      redirectToAuth(tenantId);
    }
  }, [
    username,
    setLoading,
    setButtonLoadingCallback,
    checkAccountType,
    redirectToAuth,
    t,
  ]);

  const submit = async () => {
    const searchParams = new URL(window.location.toString()).searchParams;
    const tenantId = searchParams.get('tenant_id') || 'TM';
    setButtonLoadingCallback(true);
    let selfCaptchaToken: string | undefined;
    const msg = validateUsername(username);
    const passMsg = validatePass(password);
    if (msg !== undefined || passMsg !== undefined) {
      setButtonLoadingCallback(false);
      return;
    }
    if (reCaptchaFailCount >= 3) {
      const token = recaptchaRef?.current?.getValue();
      if (!token) {
        setLoginErrorMsg(t('login.recaptcha.error'));
        setButtonLoadingCallback(false);
        recaptchaRef?.current?.reset();
        return;
      }
      recaptchaRef?.current?.reset();
      selfCaptchaToken = token;
    }
    await handler();
    try {
      setLoading(true);
      const res = await AccountApi.localLogin(
        username,
        password,
        tenantId,
        selfCaptchaToken
      );
      if (res && res.data && res.data.message === 'success') {
        setReCaptchaFailCount(0);
        localStorage.removeItem(`${username}_reCaptchaFailCount`);
        if (res.data.redirectUrl) {
          navigate(res.data.redirectUrl);
        } else {
          navigate('/');
        }
      } else {
        if (
          res.data &&
          res.data.reason === 'LoginAccount;MAX_INVALID_LOGIN_COUNT'
        ) {
          setReCaptchaFailCount(3);
          setLocalStorageItemWithExpiry(
            `${username}_reCaptchaFailCount`,
            '3',
            10 * 60 * 1000
          );
          setLoginErrorMsg(t('login.signIn.error.maxLoginCount'));
        } else {
          setReCaptchaFailCount(reCaptchaFailCount + 1);
          setLocalStorageItemWithExpiry(
            `${username}_reCaptchaFailCount`,
            (reCaptchaFailCount + 1).toString(),
            10 * 60 * 1000
          );
          setLoginErrorMsg(t('login.signIn.failed'));
        }
        setButtonLoadingCallback(false);
      }
    } catch (error) {
      setAccountType(CasAccountType.None);
      setLoginErrorMsg(t('common.error.internalError'));
      console.error(error);
      setButtonLoadingCallback(false);
    } finally {
      setLoading(false);
    }
    console.log(username, password);
  };

  return (
    <>
      <Box width={493} p="40px 40px">
        <Flex alignItems="stretch" flexDirection="row">
          <Box flex="auto" color={colorStyle.color.primary}>
            <Text fontSize="lg" lineHeight="lg" mb={32}>
              {t('login.text.signIn')}
            </Text>
            {loginErrorMsg && (
              <Box mb={16}>
                <InlineError>{loginErrorMsg}</InlineError>
              </Box>
            )}
            {redirectLoadingMsg && (
              <Box mb={16}>
                <Text color={colorStyle.color.secondary}>
                  {redirectLoadingMsg}
                </Text>
              </Box>
            )}
            <Box flexDirection="column">
              <InputControl
                name="email"
                border="1px solid"
                disabled={accountType !== CasAccountType.None}
                onBlur={handler}
                onKeyDown={async (e: any) => {
                  if (e.key === 'Enter') {
                    await handler();
                  }
                }}
                value={username}
                onChange={(e: any) => {
                  validateUsername(e.target.value);
                  setLoginErrorMsg(undefined);
                  setUsername(e.target.value);
                }}
                error={errorMsg !== undefined}
                placeholder={t('login.username.placeholder')}
                width={413}
                startAdornment={
                  <InputAdornment>
                    <Icon as={UserIcon} color={iconColor} />
                  </InputAdornment>
                }
              />
              <Box>
                {errorMsg !== undefined && (
                  <InlineError>{errorMsg}</InlineError>
                )}
              </Box>
            </Box>
            {accountType === CasAccountType.LoginAccount && (
              <Box>
                <InputControl
                  name="password"
                  border="1px solid"
                  mt={16}
                  onChange={(e: any) => {
                    setPassword(e.target.value);
                  }}
                  onKeyDown={async (e: any) => {
                    if (e.key === 'Enter') {
                      await submit();
                    }
                  }}
                  placeholder={t('login.password.placeholder')}
                  type={view ? 'text' : 'password'}
                  width={413}
                  startAdornment={
                    <InputAdornment>
                      <Icon as={LockIcon} color={iconColor} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment>
                      <ButtonBase onClick={toggleView}>
                        {view ? (
                          <Icon as={ViewIcon} color={iconColor} />
                        ) : (
                          <Icon as={ViewOffIcon} color={iconColor} />
                        )}
                      </ButtonBase>
                    </InputAdornment>
                  }
                />
                <Box>
                  {passwordErrorMsg !== undefined && (
                    <InlineError>{passwordErrorMsg}</InlineError>
                  )}
                </Box>
              </Box>
            )}
            {reCaptchaFailCount >= 3 &&
              accountType === CasAccountType.LoginAccount && (
                <Box mt={16} height="76px">
                  <ReCAPTCHA
                    theme={colorMode === 'dark' ? 'dark' : 'light'}
                    ref={recaptchaRef}
                    onChange={() => {
                      setLoginErrorMsg(undefined);
                    }}
                    sitekey={config.ui.reCaptcha.sitekey}
                  />
                </Box>
              )}
            <Button
              width={413}
              variant="primary"
              m="24px 0px"
              onClick={submit}
              disabled={buttonLoading ? true : false}
            >
              {buttonLoading ? (
                <>
                  <Spinner size="xs" />
                  <Space width="3x" />
                  <Text>{t('common.text.loading')}</Text>
                </>
              ) : (
                t('login.text.signIn')
              )}
            </Button>
            <Text>
              <Flex alignItems="stretch" justifyContent="flex-start">
                {t('login.text.forgotPassword')}:
                <Link
                  ml={6}
                  onClick={() => {
                    setResetPasswordStep(ResetPasswordStep.Forgot);
                  }}
                >
                  {t('login.text.localAccount')}
                </Link>
                {!IS_UAE && !IS_JP && (
                  <>
                    <Stack direction="row" height="4x" m="0px 8px" pt="5px">
                      <Divider variant="solid" orientation="vertical" />
                    </Stack>
                    <Link href={config.ui.loginPage.forgotPassword}>
                      {t('login.text.clpAccount')}
                    </Link>
                  </>
                )}
              </Flex>
              {accountType !== CasAccountType.None && (
                <Link
                  onClick={() => {
                    setAccountType(CasAccountType.None);
                    setUsername('');
                    setButtonLoadingCallback(false);
                  }}
                  mt={8}
                >
                  {t('login.signIn.signWithDifferentAccount')}
                </Link>
              )}
            </Text>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
